import { useCallback, useMemo } from 'react';
import type {
  ToolApprovalCounts,
  ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { ToolApprovalShowFilter } from './ToolApprovalShowFilter';
import type { ToolApprovalBulkRow } from './toolApprovalBulkIds';
import type { ToolApprovalDataTableProps } from './useToolApprovalTable';
import {
  DEFAULT_TOOL_APPROVAL_FILTER,
  getToolApprovalUrlParam,
  useToolApprovalFilterState,
} from './useToolApprovalFilterState';

/** The Show-filter props spread onto a `<DataTable>`; empty when disabled. */
export type ToolApprovalShowFilterTableProps = Omit<
  ToolApprovalDataTableProps<ToolApprovalBulkRow>,
  'rowSelection'
>;

export interface UseToolApprovalShowFilterOptions {
  /** `shadow_ai_tool_approval` flag: off → no props at all. */
  enabled: boolean;
  /** DataTable id — keys the Show filter's URL param. */
  tableId: string;
  /** True for URL-backed tables; false for the member drill-down. */
  persistInUrl: boolean;
  /** Tab totals shown on the Show options. */
  counts: ToolApprovalCounts | undefined;
}

export interface ToolApprovalShowFilterState {
  filter: ToolApprovalFilter;
  setFilter: (next: ToolApprovalFilter) => void;
  dataTableProps: ToolApprovalShowFilterTableProps;
}

/**
 * The "Show: Not approved | Approved | All" filter shared by the org tables
 * (KIR-4854) and the personal tables (KIR-4864): its state, the toolbar
 * control, the Clear-filters reset and the page reset. One copy, so a fix such
 * as KIR-5088 reaches every table that has the filter.
 */
export function useToolApprovalShowFilter({
  enabled,
  tableId,
  persistInUrl,
  counts,
}: UseToolApprovalShowFilterOptions): ToolApprovalShowFilterState {
  const { filter, setFilter } = useToolApprovalFilterState({
    urlParam: getToolApprovalUrlParam(tableId),
    persistInUrl,
  });

  const resetFilter = useCallback(
    (): void => setFilter(DEFAULT_TOOL_APPROVAL_FILTER),
    [setFilter]
  );

  const dataTableProps = useMemo<ToolApprovalShowFilterTableProps>(() => {
    if (!enabled) return {};
    return {
      toolbarEndSlot: (
        <ToolApprovalShowFilter
          value={filter}
          onChange={setFilter}
          counts={counts}
        />
      ),
      onClearFilters: resetFilter,
      // URL mode only: the Show choice lives in the URL there, so the table
      // must drop the param in the same write it uses for its own (KIR-5088) —
      // `resetFilter` alone loses that race and the filter survived a "Clear
      // filters". In drill-down (local) mode there is no param and
      // `resetFilter` is the whole story.
      ...(persistInUrl
        ? { clearFiltersUrlKeys: [getToolApprovalUrlParam(tableId)] }
        : {}),
      pageResetKey: filter,
    };
  }, [enabled, filter, setFilter, counts, resetFilter, persistInUrl, tableId]);

  return { filter, setFilter, dataTableProps };
}
